import { TFunction } from 'i18next';

import { UiEntitiesFilter } from '@/modules/onboarding/common/hooks/use-entities-query-filters';
import { EntityValidationReportResponse } from '@/modules/onboarding/common/types/entity-validation-report.response';
import { FilterConfig, PresetFilterOption } from '@/types/filters';

export enum EntitiesFilterFieldsKeys {
  SEARCH = 'q',
  PRESET = 'filter',
}

export const getEntitiesOnboardingFiltersConfig = (
  searchPlaceholderStringResource: string,
  t: TFunction,
  counts: EntityValidationReportResponse & {
    selectedCount: number;
    resolvedFilters?: Partial<Record<UiEntitiesFilter, boolean>>;
  }
): FilterConfig[] => {
  const presetFilterOptions: PresetFilterOption[] = [
    {
      label: t('authenticated.onboarding.common:filters.all', {
        count: counts.total,
      }),
      value: '',
      variant: 'default' as const,
    },
  ];

  const isDuplicatesResolved = counts.resolvedFilters?.[UiEntitiesFilter.DUPLICATES] ?? false;
  if (counts.duplicatesCount > 0 || isDuplicatesResolved) {
    presetFilterOptions.push({
      label: t('authenticated.onboarding.common:filters.duplicates', {
        count: counts.duplicatesCount,
      }),
      value: UiEntitiesFilter.DUPLICATES,
      variant: 'outlineDestructive' as const,
      resolved: isDuplicatesResolved,
    });
  }

  const isErrorsResolved = counts.resolvedFilters?.[UiEntitiesFilter.ERRORS] ?? false;
  if (counts.errorsCount > 0 || isErrorsResolved) {
    presetFilterOptions.push({
      label: t('authenticated.onboarding.common:filters.errors', {
        count: counts.errorsCount,
      }),
      value: UiEntitiesFilter.ERRORS,
      variant: 'outlineDestructive' as const,
      resolved: isErrorsResolved,
    });
  }

  const isWarningsResolved = counts.resolvedFilters?.[UiEntitiesFilter.WARNINGS] ?? false;
  if (counts.warningsCount > 0 || isWarningsResolved) {
    presetFilterOptions.push({
      label: t('authenticated.onboarding.common:filters.warnings', {
        count: counts.warningsCount,
      }),
      value: UiEntitiesFilter.WARNINGS,
      variant: 'outlineWarning' as const,
      resolved: isWarningsResolved,
    });
  }

  if (counts.selectedCount > 0) {
    presetFilterOptions.push({
      label: t('authenticated.onboarding.common:filters.selected', {
        count: counts.selectedCount,
      }),
      value: UiEntitiesFilter.SELECTED,
      variant: 'default' as const,
    });
  }

  return [
    {
      type: 'search',
      name: EntitiesFilterFieldsKeys.SEARCH,
      label: t('authenticated.onboarding.common:filters.search'),
      placeholder: t(searchPlaceholderStringResource),
      minCharacters: 2,
    },
    {
      type: 'presetFilters',
      name: EntitiesFilterFieldsKeys.PRESET,
      label: t('authenticated.onboarding.common:filters.filter'),
      options: presetFilterOptions,
    },
  ];
};
